/**
 * renderNode — declarative manifest layout renderer.
 *
 * Resolves "{{source.path}}" bindings in props against a fetched data bag
 * (a map of sourceId → data), then renders the component from CATALOG.
 *
 * Rules:
 * - Only property-path traversal (split on '.') — no eval, no function calls.
 * - Unknown component → visible "unsupported component" placeholder (no crash).
 * - Binding not found → null (component receives null for that prop).
 * - Children are rendered recursively.
 */
import type { ReactNode } from 'react'
import type { ManifestNode } from '../api/client'
import { CATALOG } from './catalog'

const BINDING_RE = /^\{\{([^}]+)\}\}$/

/**
 * Resolve a binding expression like "{{source.path.to.value}}" against the data bag.
 * "source" is the first path segment, used as the sourceId key in dataBag.
 * Returns null if the binding cannot be resolved.
 */
function resolveBinding(expr: string, dataBag: Record<string, unknown>): unknown {
  const match = BINDING_RE.exec(expr)
  if (!match) return expr // Not a binding — return as-is.

  const parts = match[1].split('.')
  if (parts.length < 2) return null

  const [sourceId, ...rest] = parts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = dataBag[sourceId]
  for (const key of rest) {
    if (current === null || current === undefined) return null
    if (typeof current !== 'object') return null
    current = (current as Record<string, unknown>)[key]
  }
  return current ?? null
}

/**
 * Resolve all prop values that are "{{…}}" binding strings.
 * Non-binding string values and non-string values pass through unchanged.
 */
function resolveProps(
  props: Record<string, unknown> | undefined,
  dataBag: Record<string, unknown>,
): Record<string, unknown> {
  if (!props) return {}
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === 'string' && BINDING_RE.test(value)) {
      result[key] = resolveBinding(value, dataBag)
    } else {
      result[key] = value
    }
  }
  return result
}

let _key = 0

/**
 * renderNode — render a single ManifestNode (and its children recursively).
 */
export function renderNode(node: ManifestNode, dataBag: Record<string, unknown>): ReactNode {
  const Comp = CATALOG[node.component]

  if (!Comp) {
    return (
      <div
        key={`unknown-${++_key}`}
        className="border border-dashed border-amber-300 bg-amber-50 rounded px-3 py-2 text-xs text-amber-700"
      >
        Unsupported component: <strong>{node.component}</strong>
      </div>
    )
  }

  const resolvedProps = resolveProps(node.props, dataBag)

  const children = node.children
    ? node.children.map((child, i) => {
        // Assign a stable-ish key by index (manifest is static).
        return <ChildWrapper key={`${node.component}-${i}`}>{renderNode(child, dataBag)}</ChildWrapper>
      })
    : undefined

  if (children && children.length > 0) {
    return <Comp {...resolvedProps}>{children}</Comp>
  }

  return <Comp {...resolvedProps} />
}

/** Thin wrapper to satisfy React key requirements. */
function ChildWrapper({ children }: { children: ReactNode }) {
  return <>{children}</>
}
